import { createReducer, on } from '@ngrx/store';
import * as LocationCardActions from '../actions';
import { findFieldById, findSectionById } from '../configs/base-utils';
import { BasicInfoCommonFieldIds, CommonSectionIds, InstallationConfigState } from '../configs/common.models';
import { resolveInstallationSection, resolveInstallationSectionId } from '../configs/installation-utils';
import { Mp2BasicInfoFieldId } from '../configs/triton/base-config-models';
import { normalizeSection, NormalizedFieldState, NormalizedFormState, NormalizedRowConfig } from '../config.models';
import { RowItemType } from '../models';
import { AutocompleteField } from '../models/field-models';
import { assignFieldValue } from '../models/utils';
import { initialState } from './state';
import { LocationStatus } from 'app/shared/models/locations';
import { InstallationType } from 'app/shared/models/location-details';
import { RainUSGSLocationTypes } from '../../location-card.constants';

export const optionsReducer = createReducer(
    initialState,
    on(LocationCardActions.loadMonitorSeries, (state, { series }) => {
        return { ...state, allMonitorSeries: series };
    }),

    on(LocationCardActions.loadCustomerSuccess, (state, { customer }) => {
        return { ...state, customer };
    }),

    on(LocationCardActions.loadLocationsSuccess, (state, { locations }) => {
        return { ...state, locations };
    }),

    on(LocationCardActions.loadAvailableDynamicSectionsSuccess, (state, { data }) => {
        return {
            ...state,
            options: {
                ...state.options,
                dynamicSections: data,
            },
        };
    }),

    on(LocationCardActions.preloadDynamicSectionsSuccess, (state, { configTemplates }) => {
        return { ...state, options: { ...state.options, loadedDynamicConfigs: configTemplates } };
    }),

    on(LocationCardActions.loadModemProvidersSuccess, (state, { data }) => {
        const providers = [...data];
        const config = state.monitorConfiguration;

        if (config && typeof config === 'object' && 'apn' in config && typeof config.apn === 'string') {
            const currentApn = config.apn;
            if (currentApn && !providers.some(p => p.name === currentApn)) {
                providers.unshift({ id: -1, name: currentApn, isChecked: false });
            }
        }

        return {
            ...state,
            options: {
                ...state.options,
                modemProviders: {
                    data: providers,
                    loading: false,
                },
            },
        };
    }),

    on(LocationCardActions.loadInstallationTypesSuccess, (state, { data }) => {
        const selectedValue = state.locationDetails?.[0]?.installationType;
        const currentMonitorSeries = state.metadata?.monitorSeriesName;
        // Filter out unpickable types, and for FLEX also filter out Channel (series 500)
        const filteredTypes = data.filter((t) => {
            const isPickable = !t.unpickable || t.value === selectedValue;
            // FLEX doesn't support Channel installation type
            const isExcludedForFlex = currentMonitorSeries?.toLowerCase() === 'flex' && t.series === 500;
            return isPickable && !isExcludedForFlex;
        });
        const previousValue = state.metadata?.previousInstallationType;

        const newState = {
            ...state,
            options: {
                ...state.options,
                installationTypes: {
                    data: filteredTypes,
                    loading: false,
                },
            },
        };

        if (previousValue) {
            const matched = pickInstallationType(previousValue, filteredTypes);
            if (matched) {
                const installationTypeField = findFieldById(newState.formState, BasicInfoCommonFieldIds.InstallationType);
                if (installationTypeField) {
                    assignFieldValue(installationTypeField, matched.value);
                }
                attachInstallationSection(matched, newState, currentMonitorSeries);
            }
            newState.metadata = { ...newState.metadata, previousInstallationType: undefined };
        }

        return newState;
    }),

    on(LocationCardActions.loadRainGauges, (state, { data }) => {
        const filteredRainGauges = data.filter((v) => v.status !== LocationStatus.Inactive && (RainUSGSLocationTypes.includes(v.seriesID)));
        filteredRainGauges.sort((a, b) => a.locationName.localeCompare(b.locationName));

        return {
            ...state,
            options: {
                ...state.options,
                rainGauges: {
                    data: filteredRainGauges,
                    loading: false,
                },
            },
            autocompleteOptions: {
                ...state.autocompleteOptions,
                rainGauges: filteredRainGauges,
            },
        };
    }),

    on(LocationCardActions.loadQFinalEntitiesSuccess, (state, { data }) => ({
        ...state,
        options: {
            ...state.options,
            qFinalEntities: {
                data,
                loading: false,
            },
        },
    })),

    on(LocationCardActions.loadCustomPropertiesSuccess, (state, { data }) => {
        return {
            ...state,
            autocompleteOptions: {
                ...state.autocompleteOptions,
                tagKeys: data,
            },
        };
    }),

    on(LocationCardActions.tagKeyChange, (state, { fieldId, newValue, isMp1 }) => {
        const formState = { ...state.formState };
        const field = findFieldById(state.formState, fieldId);

        if (field) {
            field.value = newValue;
        }

        const newTags = new Set([...state.autocompleteOptions.tagKeys, newValue].filter((v) => !!v));

        const customPropSectionId = isMp1
            ? BasicInfoCommonFieldIds.CustomPropertiesSection
            : Mp2BasicInfoFieldId.CustomPropertiesSection;

        const customPropSection = findSectionById(formState, customPropSectionId);
        const keyFieldId = isMp1 ? BasicInfoCommonFieldIds.CustomPropertyKey : Mp2BasicInfoFieldId.CustomPropertyKey;

        const usedKeys: string[] = [];
        Object.keys(customPropSection.fields)
            .filter((v) => v.includes(keyFieldId))
            .forEach((key) => {
                const keyField = customPropSection.fields[key] as AutocompleteField<string, string>;

                if (usedKeys.includes(keyField.value)) {
                    customPropSection.fields[key].errors = { duplicate: 'ADD_EDIT.DUPLICATE_TAG_NAME_ERROR' };
                } else {
                    usedKeys.push(keyField.value);
                    customPropSection.fields[key].errors = null;
                }
            });

        return {
            ...state,
            formState: formState,
            autocompleteOptions: {
                ...state.autocompleteOptions,
                tagKeys: Array.from(newTags),
            },
        };
    }),
);

function attachInstallationSection(matched: InstallationType, state: InstallationConfigState & { formState: NormalizedFormState }, monitorSeriesName?: string): void {
    const installationSeries = matched.series;
    if (installationSeries === undefined) {
        return;
    }

    const sectionId = resolveInstallationSectionId(installationSeries);
    const sectionConfig = resolveInstallationSection(installationSeries, state, monitorSeriesName);
    if (!sectionId || !sectionConfig) {
        return;
    }

    const parentSection = state.formState.config.sections.find(
        (section) => section.id === CommonSectionIds.BasicInfo,
    );
    if (!parentSection) {
        return;
    }

    const toAttachRowIndex = parentSection.rows.findIndex((row) => row.id === 'installation-type-row');
    const sectionPath = `${CommonSectionIds.BasicInfo}.${sectionId}`;

    const normalizedChild = normalizeSection(
        sectionConfig,
        { sectionId: CommonSectionIds.BasicInfo, rowId: sectionId },
        sectionPath,
    );

    const sectionField: NormalizedFieldState = {
        id: sectionId,
        name: sectionId,
        type: RowItemType.Section,
        value: null,
        path: sectionPath,
        section: normalizedChild,
    };

    parentSection.fields[sectionId] = sectionField;

    const newRow: NormalizedRowConfig = {
        id: sectionId,
        panelClass: sectionConfig.info.panelClass || '',
        fields: [sectionId],
    };

    if (toAttachRowIndex !== -1) {
        parentSection.rows.splice(toAttachRowIndex + 1, 0, newRow);
    } else {
        parentSection.rows.push(newRow);
    }
}

function pickInstallationType(currentValue: string, types: InstallationType[]): InstallationType | undefined {
    const channel = types.find(v => v.value.includes('Channel'));
    const elevation = types.find(v => v.value.includes('Elevation: 1000'));

    if (currentValue.includes('10: 1000')) {
        return elevation;
    }

    if (currentValue.includes('Channel')) {
        return channel;
    }

    const exactMatch = types.find(v => v.value === currentValue);
    if (exactMatch) {
        return exactMatch;
    }

    if (!channel) {
        return types[0];
    }

    return undefined;
}
